import { useCallback, useMemo } from "react";

import { mapKeys, snakeCase } from "lodash";

import * as Yup from "yup";

import { trackMessengerEvent } from "shared/components/Messenger/messengerUtils";
import {
  conditionallyApproveDeal,
  previewConditionalApprovalEmail,
} from "shared/lib/api/dealApi";

import { useUpload } from "../hooks";

import { DealSummary } from "../types";

import { Form } from "./types";

export const useHook = (deal: DealSummary) => {
  const { uploadFile, attachments } = useUpload();

  const init: Form = useMemo(() => {
    return {
      applicants: deal?.applicantsSummary.map(({ id }) => ({
        application: String(id),
        selected: false,
        requiresAdditionalGuarantor: false,
        requiresAdditionalAttachment: false,
        additionalAttachments: [""],
        readMessage: false,
        customMessage: undefined,
        excludeAdverseActionInEmail: false,
      })),
    };
  }, [deal]);

  const approve = async (values: Form) => {
    const payload: any = {
      data: Object.values(values.applicants)
        .filter(({ selected }) => selected)
        .map(({ readMessage, customMessage, ...rest }) => ({
          ...(readMessage && { customMessage }),
          ...rest,
        }))
        .map(({ additionalAttachments, ...rest }) => ({
          additionalAttachments: additionalAttachments
            ?.map((item) => ({
              name: item,
            }))
            .filter(({ name }) => !!name),
          ...rest,
        }))
        .map((item) => {
          return mapKeys(item, (_, key) => snakeCase(key));
        }),
      id: deal.id,
    };

    payload.attachments = attachments;

    const response = await conditionallyApproveDeal(payload);
    if (response.status === 200) {
      trackMessengerEvent("Deal Decision", {
        deal_ID: deal.id,
        decision: "Request More Info",
      });
    }
  };

  const previewIndividualEmail = useCallback(
    async (
      applicantId: number,
      includeAdverseAction?: boolean,
      formData?: any
    ) => {
      const applicantIndex = deal?.applicantsSummary.findIndex(
        ({ id }) => id === applicantId
      );
      if (applicantIndex === -1) {
        throw new Error(`Applicant with ID ${applicantId} not found`);
      }

      // Use passed form data or defaults
      const applicantFormData = formData || {
        application: String(applicantId),
        selected: true,
        requiresAdditionalGuarantor: false,
        requiresAdditionalAttachment: false,
        additionalAttachments: [""],
        readMessage: false,
        customMessage: undefined,
      };

      const singleApplicantValues = {
        applicants: deal.applicantsSummary.map(({ id }, index) => ({
          application: String(id),
          selected: index === applicantIndex,
          requiresAdditionalGuarantor:
            index === applicantIndex
              ? applicantFormData.requiresAdditionalGuarantor
              : false,
          requiresAdditionalAttachment:
            index === applicantIndex
              ? applicantFormData.requiresAdditionalAttachment
              : false,
          additionalAttachments:
            index === applicantIndex
              ? applicantFormData.additionalAttachments
              : [""],
          readMessage:
            index === applicantIndex ? applicantFormData.readMessage : false,
          customMessage:
            index === applicantIndex
              ? applicantFormData.customMessage
              : undefined,
          excludeAdverseActionInEmail: !includeAdverseAction,
        })),
      };

      const payload = Object.values(singleApplicantValues.applicants)
        .filter(({ selected }) => selected)
        .map(({ readMessage, customMessage, ...rest }) => ({
          ...(readMessage && { customMessage }),
          ...rest,
        }))
        .map(({ additionalAttachments, ...rest }) => ({
          additionalAttachments: additionalAttachments
            ?.map((item) => ({
              name: item,
            }))
            .filter(({ name }) => !!name),
          ...rest,
        }))
        .map((item) => {
          return mapKeys(item, (_, key) => snakeCase(key));
        });

      const response = await previewConditionalApprovalEmail({
        data: payload,
        id: deal.id,
      });

      // Return the single preview item
      const data = response.data[0];
      if (!data) {
        return null;
      }

      return {
        applicationId: data.applicationId,
        applicantName: data.applicantName,
        subject: data.subject,
        htmlBody: data.htmlBody,
      };
    },
    [deal]
  );

  const schema = Yup.object().shape({
    applicants: Yup.array().of(
      Yup.object()
        .shape({
          selected: Yup.boolean(),
          requiresAdditionalGuarantor: Yup.boolean(),
          requiresAdditionalAttachment: Yup.boolean(),
          additionalAttachments: Yup.array()
            .of(Yup.string())
            .when("requiresAdditionalAttachment", {
              is: true,
              then: Yup.array()
                .of(Yup.string().required("attachment can't be blank"))
                .required("you must add at least one attachment"),
              otherwise: Yup.array().of(Yup.string()),
            }),
          readMessage: Yup.boolean(),
          customMessage: Yup.string().when("readMessage", {
            is: true,
            then: Yup.string().required("message is required"),
            otherwise: Yup.string(),
          }),
        })
        .test("atleastOneChecked", function validate(value) {
          if (value.selected) {
            if (
              value.requiresAdditionalAttachment ||
              value.requiresAdditionalGuarantor ||
              value.readMessage
            ) {
              return true;
            }
            return false;
          }
          return true;
        })
    ),
  });

  return {
    approve,
    init,
    schema,
    previewIndividualEmail,
    uploadFile,
    attachments,
  };
};
